import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Examinee } from "@shared/interfaces/Examinee.interface";
import { JsonResponse } from "@shared/interfaces/JsonResponse.interface";
import { ToasterService } from "@shared/services/toaster.service";
import { BehaviorSubject, catchError, Observable, of, tap } from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class ExamineeAuthService {

    private _examineeToken: string = null;
    private _userId: string = null;

    private _currentExaminee$$: BehaviorSubject<Examinee> = new BehaviorSubject<Examinee>(null);
    public currentExaminee$: Observable<Examinee> = this._currentExaminee$$.asObservable();

    constructor(
        private _http: HttpClient,
        private _toasterService: ToasterService,
    ) {

    }

    get examineeToken(): string {
        return this._examineeToken;
    }

    set examineeToken(token: string) {
        this._examineeToken = token;
    }

    get userId(): string {
        return this._userId;
    }

    set userId(value: string) {
        this._userId = value;
    }

    set currentExaminee(examinee: Examinee) {
        this._currentExaminee$$.next(examinee);
    }

    public clearCurrentExaminee(): void {
        this._currentExaminee$$.next(null);

        this._examineeToken = null;
        this._userId = null;
    }

    public sendCodeToExaminee(prefix?: string, phoneNo?: string): Observable<JsonResponse<{ userId: string }>> {
        return this._http.post<JsonResponse<{ userId: string }>>('/examinees/send-code-to-examinee', { prefix, phoneNo, userId: this._userId }).pipe(
            tap(res => {
                if (res.success) {
                    this._userId = res.data.userId;
                }
            }),
            catchError(err => of(err))
        );
    }

    public verifyExamineeCode(code: string): Observable<JsonResponse<{ examinees: Examinee[]; token: string; userId: string }>> {
        return this._http.post<JsonResponse<{ examinees: Examinee[]; token: string; userId: string }>>('/examinees/verify-examinee-code', { code, userId: this._userId }).pipe(
            tap(res => {
                if (res.success) {
                    this._examineeToken = res.data.token;
                    this._userId = res.data.userId;
                }
            }),
            catchError(err => of(err))
        );
    }

    public getExaminee(examineeId: string): Observable<JsonResponse<Examinee>> {
        return this._http.get<JsonResponse<Examinee>>('/examinees/get-examinee', { params: { examineeId } }).pipe(
            tap(res => {
                if (res.success) {
                    this._currentExaminee$$.next(res.data); 
                    this._userId = res.data.userId;
                }
            }),
            catchError(err => of(err))
        );
    }

    public checkForActiveExamineeToken(prefix?: string, phoneNo?: string): Observable<JsonResponse<{ hasActiveToken: boolean, expiresAt: Date | null, userId: string | null }>> {
        return this._http.post<JsonResponse<{ hasActiveToken: boolean, expiresAt: Date | null, userId: string | null }>>('/examinees/check-for-active-examinee-token', {
            prefix,
            phoneNo,
            userId: this._userId
        }).pipe(
            tap(res => {
                if (res.success && res.data.userId) {
                    this._userId = res.data.userId;
                }
            }),
            catchError(err => of(err))
        );
    }

    public getActiveExamineeToken(): Observable<JsonResponse<{ examinees: Examinee[]; token: string; userId: string }>> {
        return this._http.get<JsonResponse<{ examinees: Examinee[]; token: string; userId: string }>>('/examinees/get-active-examinee-token', {
            params: {
                userId: this._userId
            }
        }).pipe(
            tap(res => {
                if (res.success) {
                    this._examineeToken = res.data.token ?? null;
                    this._userId = res.data.userId;
                }
            }),
            catchError(err => of(err))
        );
    }

    public verifyExamineeQRToken(token: string): Observable<JsonResponse<{ examinee: Examinee; token: string}>> {
        return this._http.post<JsonResponse<{ examinee: Examinee; token: string; userId: string }>>('/examinees/verify-examinee-qr-token', { token }).pipe(
            tap(res => {
                if (res.success) {
                    this._examineeToken = res.data.token ?? null;
                    this._userId = res.data.examinee.userId;
                    
                    this._currentExaminee$$.next(res.data.examinee ?? null);
                }
            }),
            catchError(err => of(err))
        );
    }

    public getAddresses(examineeId: string): Observable<JsonResponse<any>> {
        return this._http.get<JsonResponse>(`/examinees/addresses`, { params: { examineeId } }).pipe(
            catchError(err => of(err))
        );
    }
}
